import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminFetch } from "../../api/adminApi";
import { AdminCard, AdminLabel, AdminPageHeader } from "./AdminPageHeader";
import AdminRichEditor, { AdminHtmlPreviewFrame } from "./AdminRichEditor";
import { pitchCustomerLangLabel } from "../../constants/customerLanguages";

export default function AdminEditorSplit() {
  const { customerId, articleId } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [headline, setHeadline] = useState("");
  const [body, setBody] = useState("");
  const [summary, setSummary] = useState("");
  const [clear, setClear] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminFetch(
          `/v1/admin/customers/${customerId}/articles/${articleId}`,
        );
        if (cancelled) return;
        setPayload(d);
        const ca = d.customer_article;
        setHeadline(ca?.headline_override ?? "");
        setBody(ca?.body_html_override ?? "");
        setSummary(ca?.summary_override ?? "");
        setClear(false);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, articleId]);

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    try {
      await adminFetch(
        `/v1/admin/customers/${customerId}/articles/${articleId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            headline_override: headline || null,
            body_html_override: body || null,
            summary_override: summary || null,
            clear_overrides: clear,
          }),
        },
      );
      setOk(true);
      if (clear) {
        setHeadline("");
        setBody("");
        setSummary("");
        setClear(false);
      }
    } catch (e) {
      setErr(e.data?.error || e.message);
    }
  };

  if (!payload && !err) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#CCFF00]/30 border-t-[#CCFF00]" />
      </div>
    );
  }
  if (err && !payload) {
    return (
      <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-300">
        {err}
      </div>
    );
  }

  const leftJson =
    payload.raw_goalise != null
      ? JSON.stringify(payload.raw_goalise, null, 2)
      : payload.article.raw_payload;

  return (
    <div>
      <AdminPageHeader
        kicker="Delivery"
        title="Split-view editor"
        description={`${payload.customer.name} — tune HTML delivery for this customer. The canonical article stays untouched; overrides + machine translation decide the public feed.`}
      >
        <button
          type="button"
          className="pitch-admin-btn-ghost"
          onClick={() => navigate("/admin/feed")}
        >
          ← Feed
        </button>
      </AdminPageHeader>

      <div className="mb-6 rounded-xl border border-white/[0.08] bg-black/40 px-5 py-4 text-sm leading-relaxed text-slate-400">
        <span className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">
          Versions
        </span>
        <p className="mt-2 text-slate-300">
          <strong className="text-white">Original</strong> — global canonical
          from Goalise (left column: wire payload + canonical HTML).{" "}
          <strong className="text-white">Localized delivery</strong> —{" "}
          {pitchCustomerLangLabel(payload.customer.target_lang)} via DeepL,
          then your overrides when set.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <AdminCard>
          <div className="mb-4 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[#CCFF00] shadow-[0_0_10px_#CCFF00]" />
            <h2 className="text-[10px] font-black uppercase tracking-[0.28em] text-[#CCFF00]">
              Reference · read only
            </h2>
          </div>
          <p className="mb-4 text-sm font-medium leading-snug text-white">
            {payload.article.headline}
          </p>
          <p className="mb-3 font-mono-data text-[10px] uppercase tracking-widest text-slate-600">
            Provider payload
          </p>
          <pre className="mb-6 max-h-[280px] overflow-auto rounded-lg border border-white/[0.06] bg-black/50 p-4 font-mono-data text-[10px] leading-relaxed text-slate-400 [overflow-wrap:anywhere]">
            {leftJson}
          </pre>
          <p className="mb-2 font-mono-data text-[10px] uppercase tracking-widest text-slate-600">
            Canonical body (HTML)
          </p>
          <pre className="mb-4 max-h-36 overflow-auto rounded-lg border border-white/[0.06] bg-black/35 p-3 font-mono-data text-[10px] leading-relaxed text-slate-500 [overflow-wrap:anywhere]">
            {payload.article.body_html}
          </pre>
          <AdminHtmlPreviewFrame
            html={payload.article.body_html}
            label="Canonical · rendered"
          />
        </AdminCard>

        <AdminCard>
          <div className="mb-4 flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
            <h2 className="text-[10px] font-black uppercase tracking-[0.28em] text-white">
              Customer overrides
            </h2>
          </div>
          <form onSubmit={save} className="space-y-5">
            <div>
              <AdminLabel>Headline override</AdminLabel>
              <input
                className="pitch-admin-input"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                disabled={clear}
              />
            </div>
            <div>
              <AdminLabel>Summary override</AdminLabel>
              <input
                className="pitch-admin-input"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                disabled={clear}
              />
            </div>
            <div>
              <AdminLabel>Body override</AdminLabel>
              <AdminRichEditor
                value={body}
                onChange={setBody}
                disabled={clear}
                placeholder="Optional full HTML override for this customer…"
                helperText="Rich editor with live preview. Leave empty to fall back to machine translation, then canonical."
                size="large"
                showPreview={!clear}
              />
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-white/[0.08] bg-black/30 px-4 py-3 transition-colors hover:border-white/15">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-white/20 bg-black text-[#CCFF00] focus:ring-[#CCFF00]/40"
                checked={clear}
                onChange={(e) => setClear(e.target.checked)}
              />
              <span className="text-sm text-slate-400">
                Clear all overrides for this customer and fall back to machine /
                canonical text.
              </span>
            </label>
            {err && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {err}
              </div>
            )}
            {ok && (
              <p className="font-mono-data text-sm text-[#CCFF00]">
                Saved — customer feed will reflect overrides immediately.
              </p>
            )}
            <button type="submit" className="pitch-admin-btn-primary">
              Save overrides
            </button>
          </form>
        </AdminCard>
      </div>
    </div>
  );
}